import React, { useRef, useState } from "react";
import { http } from "@/lib/api";
import { OPTIONS, validate, toPayloadFormed, FIELD_LABELS } from "@/lib/reteForm";
import { Field, TextInput, ChoiceGroup, GroupGrid } from "@/components/ReteFormFields";

const FRIENDLY_LEVELS = [{ value: true, label: "Sì" }, { value: false, label: "No" }];

function Section({ title, children, intro }) {
  return (
    <section className="rounded-3xl border border-viola/10 dark:border-cream/10 bg-white/60 dark:bg-viola/20 p-5 sm:p-8 space-y-7">
      <div>
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        {intro && <div className="mt-2 text-sm text-viola/75 dark:text-cream/75 space-y-2">{intro}</div>}
      </div>
      {children}
    </section>
  );
}

export default function ReteForm({ initial, mode, targetId, token, onDone }) {
  const [f, setF] = useState(initial);
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const [honey, setHoney] = useState("");
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState("");
  const summaryRef = useRef(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const err = (k) => errors[k];

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    if (!consent) errs.consent = "Serve il consenso per inviare il modulo";
    setErrors(errs);
    if (Object.keys(errs).length) {
      setTimeout(() => summaryRef.current?.focus(), 0);
      return;
    }
    setSending(true);
    setServerError("");
    try {
      const data = { ...f, formed: toPayloadFormed(f.formed), email: f.email.trim().toLowerCase() };
      await http.post("/rete/requests", { type: mode, target_id: targetId || null, data, consent, website: honey, token: token || null });
      onDone();
    } catch (ex) {
      const d = ex.response?.data?.detail;
      setServerError(typeof d === "string" ? d : "Invio non riuscito: controlla i campi e riprova.");
      setTimeout(() => summaryRef.current?.focus(), 0);
    } finally {
      setSending(false);
    }
  };

  const errCount = Object.keys(errors).length;

  return (
    <form onSubmit={submit} noValidate className="space-y-8" data-testid="rete-form">
      {(errCount > 0 || serverError) && (
        <div ref={summaryRef} tabIndex={-1} role="alert" className="rounded-2xl border-2 border-red-600 bg-red-50 p-4 text-red-800 dark:bg-red-950/40 dark:text-red-200" data-testid="rete-form-errors">
          {serverError || (
            <>
              <p className="font-semibold">{errCount === 1 ? "C'è 1 campo da completare o correggere:" : `Ci sono ${errCount} campi da completare o correggere:`}</p>
              <ul className="mt-2 list-disc pl-5 text-sm">
                {Object.keys(errors).map((k) => (
                  <li key={k}><a href={k === "consent" ? "#consent" : `#${k}`} onClick={(e) => { e.preventDefault(); const el = document.getElementById(k === "consent" ? "consent" : k) || document.querySelector(`[data-testid="field-${k}"] input`); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.focus?.({ preventScroll: true }); }} className="underline">{k === "consent" ? "Consenso" : FIELD_LABELS[k]}</a></li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <Section title="Chi sei">
        <div className="grid sm:grid-cols-2 gap-6">
          <Field id="cognome" label="Cognome" hint="Iniziale maiuscola" required error={err("cognome")}>
            <TextInput id="cognome" value={f.cognome} onChange={set("cognome")} error={err("cognome")} hint autoComplete="family-name" />
          </Field>
          <Field id="nome" label="Nome" hint="Iniziale maiuscola" required error={err("nome")}>
            <TextInput id="nome" value={f.nome} onChange={set("nome")} error={err("nome")} hint autoComplete="given-name" />
          </Field>
        </div>
        <ChoiceGroup id="pronomi" legend="Pronomi" hint="Si possono selezionare più opzioni." required multiple allowOther options={OPTIONS.pronomi} value={f.pronomi} onChange={set("pronomi")} error={err("pronomi")} />
        <ChoiceGroup id="qualifica" legend="Qualifica professionale" hint="Selezionare più opzioni se necessario. Il modulo è riservato alle professioni normate e iscritte a un Albo." required multiple allowOther options={OPTIONS.qualifica} value={f.qualifica} onChange={set("qualifica")} error={err("qualifica")} />
        <Field id="specifiche" label="Specifiche sulla qualifica professionale" required error={err("specifiche")}
          hint="Es: orientamento cognitivo-comportamentale, EMDR, DCA, arteterapia, mindfulness, malattie croniche, endocrinologia, lavoro con gruppi, supporto al coming out, elaborazione lutti, valutazioni ADHD nell'adulto, ecc.">
          <TextInput id="specifiche" multiline value={f.specifiche} onChange={set("specifiche")} error={err("specifiche")} hint />
        </Field>
        <ChoiceGroup id="trauma" legend="Formazione specialistica per il lavoro clinico con il trauma" hint={'Es: se sono in formazione sul tema, posso scegliere "più sì che no" a seconda delle competenze che ritengo di avere.'} required options={OPTIONS.trauma} value={f.trauma} onChange={set("trauma")} error={err("trauma")} />
        <ChoiceGroup id="eta" legend="Età dell'utenza" hint="Se si lavora con tutte le fasce d'età, selezionare l'ultima." required multiple options={OPTIONS.eta} value={f.eta} onChange={set("eta")} error={err("eta")} />
        <ChoiceGroup id="lavoro_con" legend="Lavoro con" required multiple options={OPTIONS.lavoro_con} value={f.lavoro_con} onChange={set("lavoro_con")} error={err("lavoro_con")} />
        <ChoiceGroup id="lingua" legend="Terapia / lavoro in lingua" hint="Selezionare SOLO le lingue con cui ci si sente di muoversi nel setting di lavoro." required multiple allowOther options={OPTIONS.lingua} value={f.lingua} onChange={set("lingua")} error={err("lingua")} />
      </Section>

      <Section title="Lavoro con i gruppi marginalizzati" intro={<p>Indica il grado di informazione e formazione per il lavoro con popolazioni minoritarie, che presuppongono una preparazione specifica e culturalmente umile.</p>}>
        <GroupGrid id="friendly" legend="Friendly" levels={FRIENDLY_LEVELS} value={f.friendly} onChange={set("friendly")} error={err("friendly")} highlightNew={mode === "update"}
          hint={<>
            <p><strong>Friendly</strong> significa senza formazione specifica ma apertǝ e sufficientemente informatǝ sul tema: avere idea dell'intersezione tra privilegi e oppressioni di ogni persona e lavorare con un approccio affermativo e accogliente.</p>
            <p>Per essere friendly non è necessario "non avere pregiudizi", ma essere consapevoli di averne e di come possono impattare sul lavoro.</p>
          </>} />
        <GroupGrid id="formed" legend="Formed" levels={OPTIONS.formed} value={f.formed} onChange={set("formed")} error={err("formed")} highlightNew={mode === "update"}
          hint={<>
            <p><strong>Formed</strong> significa informatǝ e formatǝ specificatamente tramite corsi, workshop, scuole, master, letture autodidattiche, ecc. e anche tramite un percorso di decostruzione personale.</p>
            <p>Gruppi come "persone neurodivergenti" o "persone disabili" sono molto ampi: puoi specificare i sottogruppi nelle specifiche sulla qualifica. Le persone con malattie croniche rientrano nel gruppo "persone disabili".</p>
          </>} />
      </Section>

      <Section title="Dati di contatto">
        <Field id="city" label="Città" hint="Specificare la provincia. Se lavori in più città, separale con una virgola." required error={err("city")}>
          <TextInput id="city" value={f.city} onChange={set("city")} error={err("city")} hint />
        </Field>
        <ChoiceGroup id="in_studio" legend="Disponibilità in studio" hint="L'accessibilità è riferita alle persone con disabilità motoria." required options={OPTIONS.in_studio} value={f.in_studio} onChange={set("in_studio")} error={err("in_studio")} />
        <ChoiceGroup id="online" legend="Disponibilità online" required options={["Sì", "No"]} value={f.online === null ? "" : f.online ? "Sì" : "No"} onChange={(v) => set("online")(v === "Sì")} error={err("online")} />
        <ChoiceGroup id="tempi" legend="Tempistiche di disponibilità" hint="Se cambiano spesso, meglio selezionare l'ultima opzione." required options={OPTIONS.tempi} value={f.tempi} onChange={set("tempi")} error={err("tempi")} />
        <Field id="agevolate" label="Tariffe agevolate" hint="Scrivi sì se prevedi un tariffario calmierato per studenti, persone disoccupate, con basso ISEE, ecc. Altrimenti no, oppure specifica." required error={err("agevolate")}>
          <TextInput id="agevolate" value={f.agevolate} onChange={set("agevolate")} error={err("agevolate")} hint />
        </Field>
        <div className="grid sm:grid-cols-2 gap-6">
          <Field id="email" label="E-mail" hint="Tutta in minuscolo" required error={err("email")}>
            <TextInput id="email" type="email" value={f.email} onChange={set("email")} error={err("email")} hint autoComplete="email" />
          </Field>
          <Field id="whatsapp" label="WhatsApp" hint="Il numero, oppure NO" required error={err("whatsapp")}>
            <TextInput id="whatsapp" value={f.whatsapp} onChange={set("whatsapp")} error={err("whatsapp")} hint inputMode="tel" />
          </Field>
          <Field id="telegram" label="Telegram" hint="Il numero, oppure NO" required error={err("telegram")}>
            <TextInput id="telegram" value={f.telegram} onChange={set("telegram")} error={err("telegram")} hint />
          </Field>
          <Field id="instagram" label="Instagram" hint="In minuscolo con la @ davanti, oppure NO" required error={err("instagram")}>
            <TextInput id="instagram" value={f.instagram} onChange={set("instagram")} error={err("instagram")} hint />
          </Field>
          <Field id="facebook" label="Facebook" hint="Il link, oppure NO" required error={err("facebook")}>
            <TextInput id="facebook" value={f.facebook} onChange={set("facebook")} error={err("facebook")} hint />
          </Field>
          <Field id="sito" label="Sito" hint="Il link, oppure NO" required error={err("sito")}>
            <TextInput id="sito" value={f.sito} onChange={set("sito")} error={err("sito")} hint />
          </Field>
        </div>
        <Field id="altro" label="Altro da segnalare" hint="Es: attivismo, divulgazione, premi/riconoscimenti, associazione o équipe di cui fai parte, ecc.">
          <TextInput id="altro" multiline value={f.altro} onChange={set("altro")} hint />
        </Field>
      </Section>

      <div className="hidden" aria-hidden>
        <label>Non compilare<input tabIndex={-1} autoComplete="off" value={honey} onChange={(e) => setHoney(e.target.value)} /></label>
      </div>

      <div className="space-y-4">
        <label className="flex items-start gap-3 text-sm">
          <input id="consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-fucsia" data-testid="rete-form-consent" aria-describedby={errors.consent ? "consent-error" : undefined} />
          <span>Dichiaro che le informazioni sono un'autodichiarazione di cui mi assumo la responsabilità e acconsento alla loro pubblicazione nella Rete GSRD, secondo la <a href="/privacy-policy" target="_blank" rel="noreferrer" className="underline">privacy policy</a>. *</span>
        </label>
        {errors.consent && <p id="consent-error" className="text-sm font-semibold text-red-700 dark:text-red-300" role="alert">{errors.consent}</p>}
        <p className="text-sm text-viola/70 dark:text-cream/70">Moduli incompleti o inaccurati non saranno pubblicati. Per dubbi: <a className="underline" href="mailto:dania.piras90@gmail.com">dania.piras90@gmail.com</a></p>
        <button type="submit" disabled={sending} className="w-full sm:w-auto rounded-full bg-fucsia text-white px-8 py-3.5 font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity" data-testid="rete-form-submit">
          {sending ? "Invio in corso…" : mode === "update" ? "Pubblica l'aggiornamento" : "Invia la richiesta"}
        </button>
      </div>
    </form>
  );
}
